import { debounce, put, call } from 'redux-saga/effects'

import { TypedSagaDuck } from './FiltersDuck.types'
import { FILTERS_ACTIONS } from './FiltersDuck.actions'

import { createInitiateLoading } from '../DataDuck/DataDuck.actionCreators'

import {
  updateState,
  updateCurrentData,
  updatePortalModified,
  saveCurrentSchema,
  updateCurrentBlock,
  getCurrentBlock
} from '@aytm/app/insights/dashboard/helpers/requests'

import {
  updatePortalFilters,
  updatePanelFilters,
  resetPanelFilters,
  updateMinSampleSize,
  getPortalFilters,
  getPanelFilters
} from '@aytm/app/insights/dashboard/helpers/filters'

import { compareObjects } from '@aytm/app/insights/dashboard/lib/util'

import { duckName } from './constants'
import { FiltersOrderedMap } from '@aytm/app/insights/dashboard/models/BlockModel'

const FILTERS_DEBOUNCE_TIMEOUT = 500

const FiltersDuck = TypedSagaDuck(duckName, 'insights-dashboard')

// actions

FiltersDuck.action(FILTERS_ACTIONS.APPLY_CURRENT)
FiltersDuck.action(FILTERS_ACTIONS.UPDATE_CURRENT)
FiltersDuck.action(FILTERS_ACTIONS.CLEAR_ALL_PORTAL)
FiltersDuck.action(FILTERS_ACTIONS.CLEAR_ALL_PANEL)
FiltersDuck.action(FILTERS_ACTIONS.CANCEL_CURRENT)
FiltersDuck.action(FILTERS_ACTIONS.SET_MIN_SAMPLE_SIZE)
FiltersDuck.action(FILTERS_ACTIONS.RESET)
FiltersDuck.action(FILTERS_ACTIONS.TOGGLE_FILTERED_ROW_AVERAGES)

// reducers

FiltersDuck.reducer(FILTERS_ACTIONS.APPLY_CURRENT, (state, action) => {
  let resultState = state
  const { portalCurrentFilters, panelCurrentFilters } = action.payload
  if (portalCurrentFilters) {
    resultState = updatePortalFilters(resultState, portalCurrentFilters)
    resultState = updateCurrentData(resultState, { portalFiltersChanged: false })
    resultState = updatePortalModified(resultState)
  }
  if (panelCurrentFilters) {
    resultState = updatePanelFilters(resultState, panelCurrentFilters)
    resultState = updateCurrentData(resultState, { panelFiltersChanged: false })
  }
  return resultState
})

FiltersDuck.reducer(FILTERS_ACTIONS.CLEAR_ALL_PORTAL, state => {
  let resultState = state
  resultState = updatePortalFilters(resultState, FiltersOrderedMap())
  resultState = updateCurrentData(resultState, {
    portalCurrentFilters: FiltersOrderedMap(),
    portalFiltersChanged: false
  })
  resultState = updateState(resultState, { portalModified: true })
  return resultState
})

FiltersDuck.reducer(FILTERS_ACTIONS.CLEAR_ALL_PANEL, state => {
  let resultState = state
  resultState = updatePanelFilters(resultState, FiltersOrderedMap())
  resultState = updateCurrentData(resultState, { panelCurrentFilters: FiltersOrderedMap(), panelFiltersChanged: false })
  return resultState
})

FiltersDuck.reducer(FILTERS_ACTIONS.UPDATE_CURRENT, (state, action) => {
  let resultState = state
  const { portalCurrentFilters, panelCurrentFilters } = action.payload
  if (portalCurrentFilters) {
    const portalModelFilters = getPortalFilters(resultState)
    const portalFiltersChanged = !compareObjects(portalCurrentFilters?.toJS(), portalModelFilters?.toJS())
    resultState = updateCurrentData(resultState, { portalCurrentFilters, portalFiltersChanged })
  }
  if (panelCurrentFilters) {
    const panelModelFilters = getPanelFilters(resultState)
    const panelFiltersChanged = !compareObjects(panelCurrentFilters?.toJS(), panelModelFilters?.toJS())
    resultState = updateCurrentData(resultState, { panelCurrentFilters, panelFiltersChanged })
  }
  return resultState
})

FiltersDuck.reducer(FILTERS_ACTIONS.CANCEL_CURRENT, (state, action) => {
  let resultState = state
  const { type } = action.payload
  if ('portal' === type) {
    const portalFilters = getPortalFilters(resultState)
    resultState = updateCurrentData(resultState, {
      portalCurrentFilters: portalFilters.clone(),
      portalFiltersChanged: false
    })
  }
  if ('panel' === type) {
    const panelFilters = getPanelFilters(resultState)
    resultState = updateCurrentData(resultState, {
      panelCurrentFilters: panelFilters.clone(),
      panelFiltersChanged: false
    })
  }
  return resultState
})

FiltersDuck.reducer(FILTERS_ACTIONS.RESET, state => resetPanelFilters(state))

FiltersDuck.reducer(FILTERS_ACTIONS.SET_MIN_SAMPLE_SIZE, (state, action) => {
  const { minSampleSize } = action.payload
  return updateMinSampleSize(state, minSampleSize)
})

FiltersDuck.reducer(FILTERS_ACTIONS.TOGGLE_FILTERED_ROW_AVERAGES, state => {
  const block = getCurrentBlock(state)
  const filteredRowAverages = !(block?.get('filteredRowAverages') || false)
  return updateCurrentBlock(state, { filteredRowAverages })
})

// sagas

FiltersDuck.saga(function* run(duckFace) {
  yield debounce(
    FILTERS_DEBOUNCE_TIMEOUT,
    [
      duckFace.action[FILTERS_ACTIONS.APPLY_CURRENT].actionType,
      duckFace.action[FILTERS_ACTIONS.RESET].actionType,
      duckFace.action[FILTERS_ACTIONS.SET_MIN_SAMPLE_SIZE].actionType,
      duckFace.action[FILTERS_ACTIONS.CLEAR_ALL_PANEL].actionType,
      duckFace.action[FILTERS_ACTIONS.CLEAR_ALL_PORTAL].actionType,
      duckFace.action[FILTERS_ACTIONS.TOGGLE_FILTERED_ROW_AVERAGES].actionType
    ],
    function* runOnce(action) {
      yield call(saveCurrentSchema, action)
      yield put(createInitiateLoading())
    }
  )
})

FiltersDuck.saga(function* run(duckFace) {
  yield debounce(
    FILTERS_DEBOUNCE_TIMEOUT,
    [duckFace.action[FILTERS_ACTIONS.CANCEL_CURRENT].actionType],
    function* runOnce(action) {
      yield call(saveCurrentSchema, action)
    }
  )
})

// module export

export default FiltersDuck
